/**
 * EhrToolBlock — Expandable card for EHR/FHIR tool call results.
 *
 * Shows a clinician-friendly summary of FHIR resources grouped by type.
 * Full JSON is available via the Copy button for audit purposes.
 *
 * The backend (Pydantic model_dump) returns snake_case field names
 * (e.g. resource_type, medication_codeable_concept, dosage_instruction)
 * rather than FHIR camelCase, so all accessors handle both conventions.
 */

import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  FileText,
  Loader2,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { formatToolName } from '@heidi/evidence/data';
import { LOADING_STATUSES } from './UnsupportedToolBlock';

export interface EhrToolBlockProps {
  toolType: string;
  status?: string;
  resultData?: Record<string, unknown>;
  errorMessage?: string;
  className?: string;
}

const ERROR_STATUSES = new Set(['error', 'failed']);

const getVisualState = (status?: string): 'loading' | 'completed' | 'error' => {
  if (!status) return 'completed';
  const normalised = status.toLowerCase();
  if (LOADING_STATUSES.has(normalised)) return 'loading';
  if (ERROR_STATUSES.has(normalised)) return 'error';
  return 'completed';
};

// ---------------------------------------------------------------------------
// Resource extraction — handles both backend wrapper shapes and field casing
// ---------------------------------------------------------------------------

type RawResource = Record<string, unknown>;

function getResourceType(r: RawResource): string | undefined {
  const rt = r.resource_type ?? r.resourceType;
  return typeof rt === 'string' ? rt : undefined;
}

function get(r: RawResource, camel: string, snake: string): unknown {
  return r[snake] ?? r[camel];
}

function extractResources(data: Record<string, unknown>): RawResource[] {
  // ml-scribe search: { resources: [...], count: N }
  if (Array.isArray(data.resources)) {
    return (data.resources as RawResource[]).filter(
      (r) => r != null && getResourceType(r) != null
    );
  }
  // ml-scribe read: { resource: { resource_type: "...", ... } }
  const single = data.resource as RawResource | undefined;
  if (single && getResourceType(single)) {
    return [single];
  }
  // Standard FHIR Bundle
  if ((data.resourceType === 'Bundle' || data.resource_type === 'Bundle') && Array.isArray(data.entry)) {
    return (data.entry as Array<{ resource?: RawResource }>)
      .map((e) => e.resource)
      .filter((r): r is RawResource => r != null && getResourceType(r) != null);
  }
  // Bare resource
  if (getResourceType(data)) {
    return [data];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Resource-specific formatters — extract only what clinicians need to see.
// Each handles both snake_case (Pydantic) and camelCase (FHIR) field names.
// ---------------------------------------------------------------------------

type CodeableConcept = { text?: string; coding?: Array<{ display?: string; code?: string }> };

function getCodeText(r: RawResource, camel: string, snake: string): string | undefined {
  const cc = (get(r, camel, snake) ?? r.code) as CodeableConcept | undefined;
  return cc?.text || cc?.coding?.[0]?.display;
}

function formatMedication(r: RawResource): { name: string; dose?: string; status?: string } {
  const concept = (get(r, 'medicationCodeableConcept', 'medication_codeable_concept')) as CodeableConcept | undefined;
  const name = concept?.text || concept?.coding?.[0]?.display || 'Unknown medication';
  const instructions = get(r, 'dosageInstruction', 'dosage_instruction') as Array<{ text?: string }> | undefined;
  return {
    name,
    dose: Array.isArray(instructions) ? instructions[0]?.text : undefined,
    status: r.status as string | undefined,
  };
}

function formatCondition(r: RawResource): { name: string; status?: string } {
  const code = (r.code) as CodeableConcept | undefined;
  const clinicalStatus = (get(r, 'clinicalStatus', 'clinical_status')) as CodeableConcept | undefined;
  return {
    name: code?.text || code?.coding?.[0]?.display || 'Unknown condition',
    status: clinicalStatus?.coding?.[0]?.code,
  };
}

type ObservationComponent = {
  code?: CodeableConcept;
  value_quantity?: { value?: number; unit?: string };
  valueQuantity?: { value?: number; unit?: string };
  value_string?: string;
  valueString?: string;
};

function formatComponentValue(c: ObservationComponent): string | undefined {
  const vq = c.value_quantity ?? c.valueQuantity;
  const vs = c.value_string ?? c.valueString;
  if (vq?.value != null) return `${vq.value} ${vq.unit || ''}`.trim();
  return vs ?? undefined;
}

function formatObservation(r: RawResource): {
  name: string;
  value?: string;
  date?: string;
  components?: Array<{ name: string; value?: string }>;
} {
  const code = (r.code) as CodeableConcept | undefined;
  const vq = (get(r, 'valueQuantity', 'value_quantity')) as { value?: number; unit?: string } | undefined;
  const vs = (get(r, 'valueString', 'value_string')) as string | undefined;
  const date = (get(r, 'effectiveDateTime', 'effective_date_time') ?? get(r, 'issued', 'issued')) as string | undefined;

  const topValue = vq ? `${vq.value} ${vq.unit || ''}`.trim() : vs;

  // Panel observations (CBC, CMP, etc.) store individual results in component[]
  const rawComponents = (r.component ?? r.components) as ObservationComponent[] | undefined;
  let components: Array<{ name: string; value?: string }> | undefined;
  if (!topValue && Array.isArray(rawComponents) && rawComponents.length > 0) {
    components = rawComponents
      .map((c) => ({
        name: c.code?.text || c.code?.coding?.[0]?.display || 'Component',
        value: formatComponentValue(c),
      }))
      .filter((c) => c.value != null);
  }

  return {
    name: code?.text || code?.coding?.[0]?.display || 'Unknown observation',
    value: topValue,
    date,
    components: components?.length ? components : undefined,
  };
}

function formatAllergy(r: RawResource): { substance: string; reaction?: string; severity?: string } {
  const code = (r.code) as CodeableConcept | undefined;
  const reactions = (r.reaction) as Array<{
    manifestation?: CodeableConcept[];
    severity?: string;
  }>  | undefined;
  const first = Array.isArray(reactions) ? reactions[0] : undefined;
  const mf = first?.manifestation?.[0];
  return {
    substance: code?.text || code?.coding?.[0]?.display || 'Unknown allergen',
    reaction: mf?.text || mf?.coding?.[0]?.display,
    severity: first?.severity,
  };
}

function formatPatient(r: RawResource): {
  name: string;
  dob?: string;
  gender?: string;
  phone?: string;
  address?: string;
} {
  const names = (r.name) as Array<{ text?: string; given?: string[]; family?: string }> | undefined;
  const first = Array.isArray(names) ? names[0] : undefined;
  const fullName = first?.text
    || [first?.given?.join(' '), first?.family].filter(Boolean).join(' ')
    || 'Unknown patient';

  const telecoms = (r.telecom) as Array<{ system?: string; value?: string }> | undefined;
  const phone = telecoms?.find((t) => t.system === 'phone')?.value;

  const addresses = (r.address) as Array<{ text?: string; line?: string[]; city?: string; state?: string; postalCode?: string; postal_code?: string }> | undefined;
  const addr = Array.isArray(addresses) ? addresses[0] : undefined;
  const address = addr?.text
    || [addr?.line?.join(', '), addr?.city, addr?.state, addr?.postalCode ?? addr?.postal_code]
        .filter(Boolean)
        .join(', ')
    || undefined;

  return {
    name: fullName,
    dob: (get(r, 'birthDate', 'birth_date')) as string | undefined,
    gender: (r.gender) as string | undefined,
    phone,
    address: address || undefined,
  };
}

// ---------------------------------------------------------------------------
// Group heading labels
// ---------------------------------------------------------------------------

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  MedicationRequest: 'Medications',
  MedicationStatement: 'Medications',
  Condition: 'Conditions',
  Observation: 'Observations',
  AllergyIntolerance: 'Allergies',
  Patient: 'Patient',
};

function resourceGroupLabel(type: string, count: number): string {
  const label = RESOURCE_TYPE_LABELS[type] || formatToolName(type);
  return `${label} (${count})`;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ value }: { value: string }): JSX.Element {
  const isActive = value === 'active';
  return (
    <TypographyCaption
      className={cn(
        'rounded-full px-1.5 py-0.5',
        isActive
          ? 'bg-validation-success-muted text-validation-success'
          : 'bg-surface-hover text-text-tertiary'
      )}
    >
      {value}
    </TypographyCaption>
  );
}

// ---------------------------------------------------------------------------
// ResourceRow
// ---------------------------------------------------------------------------

function ResourceRow({ resource, type }: { resource: RawResource; type: string }): JSX.Element {
  if (type === 'MedicationRequest' || type === 'MedicationStatement') {
    const med = formatMedication(resource);
    return (
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <TypographyP2Bold className="min-w-0 truncate text-text-primary">{med.name}</TypographyP2Bold>
        <span className="flex shrink-0 items-center gap-2">
          {med.dose && <TypographyCaption className="max-w-[160px] truncate text-text-tertiary">{med.dose}</TypographyCaption>}
          {med.status && <StatusBadge value={med.status} />}
        </span>
      </div>
    );
  }

  if (type === 'Condition') {
    const cond = formatCondition(resource);
    return (
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <TypographyP2Bold className="min-w-0 truncate text-text-primary">{cond.name}</TypographyP2Bold>
        {cond.status && (
          <span className="shrink-0">
            <StatusBadge value={cond.status} />
          </span>
        )}
      </div>
    );
  }

  if (type === 'Observation') {
    const obs = formatObservation(resource);
    if (obs.components) {
      return (
        <div className="flex flex-col gap-0.5 px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <TypographyP2Bold className="min-w-0 truncate text-text-primary">{obs.name}</TypographyP2Bold>
            {obs.date && <TypographyCaption className="shrink-0 text-text-tertiary">{obs.date}</TypographyCaption>}
          </div>
          <div className="mt-1 flex flex-col gap-0.5 pl-2">
            {obs.components.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <TypographyCaption className="min-w-0 truncate text-text-secondary">{c.name}</TypographyCaption>
                {c.value && <TypographyCaptionBold className="shrink-0 text-text-primary">{c.value}</TypographyCaptionBold>}
              </div>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <TypographyP2 className="min-w-0 truncate text-text-primary">{obs.name}</TypographyP2>
        <span className="flex shrink-0 items-center gap-2">
          {obs.value && <TypographyCaptionBold className="text-text-secondary">{obs.value}</TypographyCaptionBold>}
          {obs.date && <TypographyCaption className="text-text-tertiary">{obs.date}</TypographyCaption>}
        </span>
      </div>
    );
  }

  if (type === 'AllergyIntolerance') {
    const allergy = formatAllergy(resource);
    return (
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <TypographyP2Bold className="min-w-0 truncate text-text-primary">{allergy.substance}</TypographyP2Bold>
        <span className="flex shrink-0 items-center gap-2">
          {allergy.reaction && <TypographyCaption className="text-text-tertiary">{allergy.reaction}</TypographyCaption>}
          {allergy.severity && <StatusBadge value={allergy.severity} />}
        </span>
      </div>
    );
  }

  if (type === 'Patient') {
    const patient = formatPatient(resource);
    return (
      <div className="flex flex-col gap-0.5 px-2 py-1.5">
        <div className="flex items-center justify-between gap-2">
          <TypographyP2Bold className="min-w-0 truncate text-text-primary">{patient.name}</TypographyP2Bold>
          <span className="flex shrink-0 items-center gap-2">
            {patient.dob && <TypographyCaption className="text-text-tertiary">{patient.dob}</TypographyCaption>}
            {patient.gender && <TypographyCaption className="text-text-tertiary">{patient.gender}</TypographyCaption>}
          </span>
        </div>
        {(patient.phone || patient.address) && (
          <div className="flex flex-col gap-0.5 pl-2">
            {patient.phone && <TypographyCaption className="text-text-secondary">{patient.phone}</TypographyCaption>}
            {patient.address && <TypographyCaption className="truncate text-text-secondary">{patient.address}</TypographyCaption>}
          </div>
        )}
      </div>
    );
  }

  const name = getCodeText(resource, 'code', 'code') || (resource.id as string) || type;
  return (
    <TypographyP2 className="px-2 py-1.5 text-text-primary">{name}</TypographyP2>
  );
}

// ---------------------------------------------------------------------------
// ResourceTable — groups resources by type and renders rows
// ---------------------------------------------------------------------------

function ResourceTable({ resources }: { resources: RawResource[] }): JSX.Element {
  const grouped = useMemo(() => {
    const groups = new Map<string, RawResource[]>();
    for (const r of resources) {
      const type = getResourceType(r) ?? 'Unknown';
      const existing = groups.get(type) ?? [];
      existing.push(r);
      groups.set(type, existing);
    }
    return groups;
  }, [resources]);

  return (
    <div className="flex flex-col gap-3">
      {Array.from(grouped.entries()).map(([type, items]) => (
        <div key={type} className="flex flex-col gap-1">
          <TypographyCaptionBold className="px-2 text-text-tertiary">
            {resourceGroupLabel(type, items.length)}
          </TypographyCaptionBold>
          <div className="flex flex-col divide-y divide-border">
            {items.map((r, i) => (
              <ResourceRow key={`${type}-${i}`} resource={r} type={type} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EhrToolBlock — main exported component
// ---------------------------------------------------------------------------

export const EhrToolBlock = ({
  toolType,
  status,
  resultData,
  errorMessage,
  className,
}: EhrToolBlockProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visualState = getVisualState(status);
  const displayName = formatToolName(toolType);
  const hasData = resultData != null && Object.keys(resultData).length > 0;
  const resources = useMemo(() => (hasData ? extractResources(resultData!) : []), [hasData, resultData]);

  const icon = <FileText className="size-5 shrink-0 text-text-tertiary" aria-hidden="true" />;

  if (visualState === 'error') {
    return (
      <div
        className={cn(
          'flex w-full flex-col rounded-lg border border-validation-destructive-border bg-validation-destructive-muted',
          'transition-all duration-200',
          className
        )}
        data-testid="ehr-tool-block"
        data-status="error"
        data-tool-type={toolType}
      >
        <div className="flex items-center gap-3 p-4">
          <FileText className="size-5 shrink-0 text-validation-destructive" aria-hidden="true" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <TypographyP2Bold className="truncate text-text-primary">{displayName}</TypographyP2Bold>
            <div className="flex items-center gap-1.5">
              <AlertCircle className="size-3 text-validation-destructive" />
              <TypographyCaption className="text-validation-destructive">
                Action failed
              </TypographyCaption>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (visualState === 'loading') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="ehr-tool-block"
        data-status="loading"
        data-tool-type={toolType}
      >
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2Bold className="truncate text-text-primary">{displayName}</TypographyP2Bold>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">Fetching EHR data…</TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-lg border border-border bg-surface-accent',
        'transition-all duration-200',
        className
      )}
      data-testid="ehr-tool-block"
      data-status="completed"
      data-tool-type={toolType}
    >
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-3 p-4 text-left',
          hasData && 'cursor-pointer transition-colors hover:bg-surface-hover'
        )}
        onClick={hasData ? () => setIsExpanded(!isExpanded) : undefined}
        disabled={!hasData}
        aria-expanded={isExpanded}
      >
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2Bold className="truncate text-text-primary">{displayName}</TypographyP2Bold>
          <div className="flex items-center gap-1.5">
            <CircleCheck className="size-3 text-validation-success" />
            <TypographyCaption className="text-text-tertiary">
              {resources.length > 0
                ? `${resources.length} ${resources.length === 1 ? 'result' : 'results'}`
                : 'Completed'}
            </TypographyCaption>
          </div>
        </div>
        {hasData && (
          isExpanded
            ? <ChevronUp className="size-4 shrink-0 text-text-tertiary" />
            : <ChevronDown className="size-4 shrink-0 text-text-tertiary" />
        )}
      </button>

      {isExpanded && hasData && (
        <div className="border-t border-border animate-in fade-in slide-in-from-top-1">
          <div className="max-h-80 overflow-y-auto p-2">
            {resources.length > 0 ? (
              <ResourceTable resources={resources} />
            ) : (
              <TypographyCaption className="px-2 py-3 text-text-tertiary">
                Data returned successfully.
              </TypographyCaption>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
